import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, Typography, Row, Col, Statistic, Button, DatePicker, Space, Select, Spin, Alert, Tag, Modal, Progress } from 'antd';
import Table from 'components/Common/Table';
import { ShoppingCartOutlined, TrophyOutlined, BarChartOutlined, DownloadOutlined, FileExcelOutlined, FileZipOutlined, CheckCircleOutlined, CloseCircleOutlined, StopOutlined } from '@ant-design/icons';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import axios from 'axios';
import useIsMobile from '../../hooks/useIsMobile';
import ReportFilterModal, { DatePresetButtons, MobileFilterButton, getDateRangeFromPreset } from '../../components/Reports/ReportFilterModal';
import ExportPdfButton from '../../components/Reports/ExportPdfButton';
import useUrlFilters from '../../hooks/useUrlFilters';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

// Filters live in the URL so a filtered report can be shared as a link.
// Relative presets (this_month, last_30_days...) are kept as the preset name,
// so the link stays "last 30 days" for whoever opens it; only custom and FY
// ranges carry explicit dates.
const URL_FILTERS = {
  preset: { type: 'string', default: 'all' },
  range: { type: 'dateRange', keys: ['start_date', 'end_date'] },
  account: { type: 'string', default: 'all' },
  status: { type: 'string', default: 'PAID' },
};
const PRESETS_WITH_DATES = ['custom', 'this_fy', 'last_fy'];

const SalesReport = () => {
  const isMobile = useIsMobile();
  const reportRef = useRef(null);
  const [filterModalVisible, setFilterModalVisible] = useState(false);
  const [filters, setFilters] = useUrlFilters(URL_FILTERS);
  const filterPreset = filters.preset;
  const dateRange = PRESETS_WITH_DATES.includes(filterPreset) ? filters.range : null;
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const accountFilter = filters.account;
  const setAccountFilter = (value) => setFilters({ account: value });
  const statusFilter = filters.status;
  const setStatusFilter = (value) => setFilters({ status: value });
  const [exportModalVisible, setExportModalVisible] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportStatus, setExportStatus] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const pollIntervalRef = useRef(null);

  // Fetch sales data
  const fetchSalesData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const useDateRange = filterPreset === 'custom' || filterPreset === 'this_fy' || filterPreset === 'last_fy';
      const params = {
        filter_preset: useDateRange ? undefined : (filterPreset === 'all' ? 'all_time' : filterPreset),
        start_date: useDateRange && dateRange && dateRange[0] ? dateRange[0].format('YYYY-MM-DD') : undefined,
        end_date: useDateRange && dateRange && dateRange[1] ? dateRange[1].format('YYYY-MM-DD') : undefined,
      };

      if (accountFilter !== 'all') {
        params.customer_id = accountFilter;
      }
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }

      const response = await axios.get('/product/product/sales-analytics/', { params });

      if (response.status === 200) {
        setData(response.data);
      }
    } catch (err) {
      console.error('Error fetching sales data:', err);
      setError(err.response?.data?.error || 'Failed to fetch sales data');
    } finally {
      setLoading(false);
    }
  }, [filterPreset, dateRange, accountFilter, statusFilter]);

  // Fetch accounts list
  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const response = await axios.get('/accounts/account/', {
          params: { page_size: 1000 }
        });
        if (response.status === 200) {
          setAccounts(response.data.results || response.data || []);
        }
      } catch (err) {
        console.error('Error fetching accounts:', err);
      }
    };
    fetchAccounts();
  }, []);

  useEffect(() => {
    fetchSalesData();
  }, [fetchSalesData]);

  // Handle preset change
  const handlePresetChange = (preset) => {
    setFilters({
      preset,
      range: PRESETS_WITH_DATES.includes(preset) ? getDateRangeFromPreset(preset) : null,
    });
  };

  // Handle date range change
  const handleDateRangeChange = (dates) => {
    if (dates && dates[0] && dates[1]) {
      setFilters({ preset: 'custom', range: [dates[0], dates[1]] });
    } else {
      setFilters({ preset: 'all', range: null });
    }
  };

  // Clear all filters
  const handleClearFilters = () => {
    setFilters({ preset: 'all', range: null, account: 'all', status: 'PAID' });
  };

  // Count active filters
  const getActiveFiltersCount = () => {
    let count = 0;
    if (filterPreset !== 'all') count++;
    if (accountFilter !== 'all') count++;
    if (statusFilter !== 'all' && statusFilter !== 'PAID') count++;
    return count;
  };

  // Fetch export status when modal opens
  const fetchExportStatus = async () => {
    try {
      const response = await axios.get('/invoices/invoices/exportStatus/');
      if (response.data && response.data.status) {
        setExportStatus(response.data);
      } else {
        setExportStatus(null);
      }
    } catch (err) {
      console.error('Error fetching export status:', err);
    }
  };

  // Poll for export status
  const pollExportStatus = async (exportJobId) => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }

    const pollInterval = setInterval(async () => {
      try {
        const response = await axios.get(`/invoices/invoices/exportStatus/?export_job_id=${exportJobId}`);
        const status = response.data;
        setExportStatus(status);

        if (status.status === 'COMPLETED' || status.status === 'FAILED') {
          clearInterval(pollInterval);
          pollIntervalRef.current = null;
          setExporting(false);

          if (status.status === 'COMPLETED' && status.download_url) {
            const link = document.createElement('a');
            link.href = status.download_url;
            link.setAttribute('download', `invoices_export.zip`);
            document.body.appendChild(link);
            link.click();
            link.remove();
          }
        }
      } catch (err) {
        console.error('Error polling export status:', err);
        clearInterval(pollInterval);
        pollIntervalRef.current = null;
        setExporting(false);
      }
    }, 2000);

    pollIntervalRef.current = pollInterval;
    return pollInterval;
  };

  const handleAbortExport = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    setExporting(false);
    setExportStatus(null);
  };

  const handleExportModalOpen = () => {
    fetchExportStatus();
    setExportModalVisible(true);
  };

  const handleExport = async (format) => {
    setExporting(true);
    try {
      if (format === 'zip') {
        if (!data || !data.invoices || data.invoices.length === 0) {
          alert('No invoices available to export.');
          setExporting(false);
          return;
        }

        const invoiceIds = [...new Set(data.invoices.map(inv => inv.invoice_id))];

        if (invoiceIds.length === 0) {
          alert('No invoice IDs found in the report data.');
          setExporting(false);
          return;
        }

        const response = await axios.post('/invoices/invoices/bulkExport/', {
          invoice_ids: invoiceIds
        });

        if (response.status === 202) {
          setExportStatus({
            export_job_id: response.data.export_job_id,
            status: 'PENDING',
            total_invoices: response.data.total_invoices,
            processed_invoices: 0,
            progress: 0
          });
          pollExportStatus(response.data.export_job_id);
        }
        return;
      }

      const useDateRange = filterPreset === 'custom' || filterPreset === 'this_fy' || filterPreset === 'last_fy';
      const params = {
        filter_preset: useDateRange ? undefined : (filterPreset === 'all' ? 'all_time' : filterPreset),
        start_date: useDateRange && dateRange && dateRange[0] ? dateRange[0].format('YYYY-MM-DD') : undefined,
        end_date: useDateRange && dateRange && dateRange[1] ? dateRange[1].format('YYYY-MM-DD') : undefined,
        export: format
      };

      if (accountFilter !== 'all') {
        params.customer_id = accountFilter;
      }
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }

      const response = await axios.get('/product/product/sales-analytics/', {
        params,
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      const timestamp = dayjs().format('YYYYMMDD_HHmmss');
      const extension = format === 'excel' ? 'xlsx' : 'csv';
      link.setAttribute('download', `sales_report_${timestamp}.${extension}`);

      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setExportModalVisible(false);
    } catch (err) {
      console.error('Error exporting data:', err);
      alert('Failed to export data. Please try again.');
    } finally {
      if (format !== 'zip') {
        setExporting(false);
      }
    }
  };

  const handleDownloadExport = async () => {
    if (exportStatus && exportStatus.download_url) {
      const link = document.createElement('a');
      link.href = exportStatus.download_url;
      link.setAttribute('download', `invoices_export.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    }
  };

  // Table columns
  const columns = [
    {
      title: 'Invoice #',
      dataIndex: 'invoice_number',
      key: 'invoice_number',
      sorter: (a, b) => a.invoice_number.localeCompare(b.invoice_number),
      fixed: isMobile ? false : 'left',
      width: 120,
      render: (text, record) => (
        <Link to={`/invoices/edit/${record.invoice_id}`}>{text}</Link>
      ),
    },
    {
      title: 'Date',
      dataIndex: 'invoice_date',
      key: 'invoice_date',
      sorter: (a, b) => new Date(a.invoice_date) - new Date(b.invoice_date),
      width: 100,
    },
    {
      title: 'Customer',
      dataIndex: 'customer',
      key: 'customer',
      sorter: (a, b) => a.customer.localeCompare(b.customer),
      width: 200,
      ellipsis: true,
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (value) => `₹${value?.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}`,
      sorter: (a, b) => a.amount - b.amount,
      align: 'right',
      width: 120,
    },
    {
      title: data?.summary?.tax_rate1_name || 'Tax 1',
      dataIndex: 'tax_rate1_amount',
      key: 'tax_rate1_amount',
      render: (value) => value > 0 ? `₹${value?.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-',
      sorter: (a, b) => (a.tax_rate1_amount || 0) - (b.tax_rate1_amount || 0),
      align: 'right',
      width: 100,
      responsive: ['md'],
    },
    {
      title: data?.summary?.tax_rate2_name || 'Tax 2',
      dataIndex: 'tax_rate2_amount',
      key: 'tax_rate2_amount',
      render: (value) => value > 0 ? `₹${value?.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-',
      sorter: (a, b) => (a.tax_rate2_amount || 0) - (b.tax_rate2_amount || 0),
      align: 'right',
      width: 100,
      responsive: ['md'],
    },
    {
      title: data?.summary?.tax_rate3_name || 'Tax 3',
      dataIndex: 'tax_rate3_amount',
      key: 'tax_rate3_amount',
      render: (value) => value > 0 ? `₹${value?.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-',
      sorter: (a, b) => (a.tax_rate3_amount || 0) - (b.tax_rate3_amount || 0),
      align: 'right',
      width: 100,
      responsive: ['lg'],
    },
    {
      title: 'Total',
      dataIndex: 'total_with_tax',
      key: 'total_with_tax',
      render: (value) => `₹${value?.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}`,
      sorter: (a, b) => (a.total_with_tax || 0) - (b.total_with_tax || 0),
      align: 'right',
      width: 110,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const colorMap = {
          'PAID': 'green',
          'SENT': 'blue',
          'PARTIAL': 'orange',
          'DRAFT': 'default',
          'CANCELLED': 'default',
        };
        return (
          <Tag color={colorMap[status] || 'default'} style={status === 'CANCELLED' ? { color: '#6B7280', backgroundColor: '#6B728020' } : {}}>
            {status || 'N/A'}
          </Tag>
        );
      },
      filters: [
        { text: 'Paid', value: 'PAID' },
        { text: 'Sent', value: 'SENT' },
        { text: 'Partial', value: 'PARTIAL' },
        { text: 'Draft', value: 'DRAFT' },
        { text: 'Cancelled', value: 'CANCELLED' },
      ],
      onFilter: (value, record) => record.status === value,
      width: 100,
    },
  ];

  // Render additional filters for modal
  const renderAdditionalFilters = () => (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      <div>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>Account</Text>
        <Select
          value={accountFilter}
          onChange={(value) => setAccountFilter(value || 'all')}
          style={{ width: '100%' }}
          placeholder="Select Account"
          showSearch
          allowClear
          optionFilterProp="children"
          filterOption={(input, option) => {
            const children = option?.children;
            if (typeof children === 'string') {
              return children.toLowerCase().includes(input.toLowerCase());
            }
            return false;
          }}
        >
          <Option value="all">All Accounts</Option>
          {accounts.map(account => (
            <Option key={account.id} value={account.id}>
              {account.name}
            </Option>
          ))}
        </Select>
      </div>
      <div>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>Status</Text>
        <Select
          value={statusFilter}
          onChange={setStatusFilter}
          style={{ width: '100%' }}
          placeholder="Status"
        >
          <Option value="all">All Statuses</Option>
          <Option value="PAID">Paid</Option>
          <Option value="SENT">Sent</Option>
          <Option value="PARTIAL">Partial</Option>
          <Option value="DRAFT">Draft</Option>
          <Option value="CANCELLED">Cancelled</Option>
        </Select>
      </div>
    </Space>
  );

  // Tables for the PDF export. Passed explicitly rather than scraped from the
  // rendered tables so the export carries every row, not just the page on screen.
  const pdfTables = [
    {
      title: 'Monthly Sales Revenue',
      columns: [
        { header: 'Month', key: 'month' },
        { header: 'Revenue', key: 'revenue', align: 'right', format: 'currency2' },
      ],
      rows: data?.monthly_revenue || [],
    },
    {
      title: 'Invoice Details',
      columns: [
        { header: 'Invoice #', key: 'invoice_number' },
        { header: 'Date', key: 'invoice_date' },
        { header: 'Customer', key: 'customer' },
        { header: 'Amount', key: 'amount', align: 'right', format: 'currency2' },
        { header: data?.summary?.tax_rate1_name || 'Tax 1', key: 'tax_rate1_amount', align: 'right', format: 'currency2' },
        { header: data?.summary?.tax_rate2_name || 'Tax 2', key: 'tax_rate2_amount', align: 'right', format: 'currency2' },
        { header: data?.summary?.tax_rate3_name || 'Tax 3', key: 'tax_rate3_amount', align: 'right', format: 'currency2' },
        { header: 'Total', key: 'total_with_tax', align: 'right', format: 'currency2' },
        { header: 'Status', key: 'status' },
      ],
      rows: data?.invoices || [],
    },
  ];

  const pdfMeta = [
    { label: 'Total Revenue', value: `Rs. ${(data?.summary?.total_revenue || 0).toLocaleString('en-IN')}` },
    { label: 'Total Tax', value: `Rs. ${(data?.summary?.total_tax || 0).toLocaleString('en-IN')}` },
    { label: 'Grand Total', value: `Rs. ${(data?.summary?.total_with_tax || 0).toLocaleString('en-IN')}` },
  ];

  return (
    <div ref={reportRef} style={{
      height: 'calc(100vh - 64px)',
      overflow: 'auto',
      padding: isMobile ? '12px' : '16px 24px',
      backgroundColor: '#f0f2f5'
    }}>
      {/* Header */}
      <div style={{
        marginBottom: '16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '12px'
      }}>
        <Title level={isMobile ? 4 : 2} style={{ margin: 0 }}>Sales Report</Title>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <ExportPdfButton
            reportRef={reportRef}
            title="Sales Report"
            subtitle={data?.summary?.date_range}
            meta={pdfMeta}
            tables={pdfTables}
            fileName="sales-report"
            orientation="landscape"
            disabled={loading || !data}
          />
          {isMobile && (
            <MobileFilterButton
              onClick={() => setFilterModalVisible(true)}
              activeFiltersCount={getActiveFiltersCount()}
            />
          )}
        </div>
      </div>

      {/* Desktop Filters */}
      {!isMobile && (
        <Card size="small" style={{ marginBottom: '16px' }}>
          <Space direction="vertical" style={{ width: '100%' }} size="small">
            <div>
              <Text strong style={{ marginBottom: '8px', display: 'block', fontSize: '13px' }}>Date Range:</Text>
              <DatePresetButtons filterPreset={filterPreset} onPresetChange={handlePresetChange} />
            </div>
            <div>
              <Space wrap size="middle">
                <div>
                  <Text strong style={{ marginBottom: '8px', display: 'block', fontSize: '13px' }}>Custom Date Range:</Text>
                  <RangePicker
                    value={dateRange}
                    onChange={handleDateRangeChange}
                    format="YYYY-MM-DD"
                    size="small"
                    allowClear
                    placeholder={['Start Date', 'End Date']}
                  />
                </div>
                <div>
                  <Text strong style={{ marginBottom: '8px', display: 'block', fontSize: '13px' }}>Account:</Text>
                  <Select
                    value={accountFilter}
                    onChange={(value) => setAccountFilter(value || 'all')}
                    size="small"
                    style={{ width: 200 }}
                    placeholder="Select Account"
                    showSearch
                    allowClear
                    optionFilterProp="children"
                    filterOption={(input, option) => {
                      const children = option?.children;
                      if (typeof children === 'string') {
                        return children.toLowerCase().includes(input.toLowerCase());
                      }
                      return false;
                    }}
                  >
                    <Option value="all">All Accounts</Option>
                    {accounts.map(account => (
                      <Option key={account.id} value={account.id}>
                        {account.name}
                      </Option>
                    ))}
                  </Select>
                </div>
                <div>
                  <Text strong style={{ marginBottom: '8px', display: 'block', fontSize: '13px' }}>Status:</Text>
                  <Select
                    value={statusFilter}
                    onChange={setStatusFilter}
                    size="small"
                    style={{ width: 150 }}
                    placeholder="Status"
                  >
                    <Option value="all">All Statuses</Option>
                    <Option value="PAID">Paid</Option>
                    <Option value="SENT">Sent</Option>
                    <Option value="PARTIAL">Partial</Option>
                    <Option value="DRAFT">Draft</Option>
                    <Option value="CANCELLED">Cancelled</Option>
                  </Select>
                </div>
              </Space>
            </div>
          </Space>
        </Card>
      )}

      {/* Mobile Filter Modal */}
      <ReportFilterModal
        visible={filterModalVisible}
        onClose={() => setFilterModalVisible(false)}
        filterPreset={filterPreset}
        dateRange={dateRange}
        onChange={({ preset, range }) => setFilters({
          preset,
          range: PRESETS_WITH_DATES.includes(preset) ? range : null,
        })}
        onClearFilters={handleClearFilters}
        title="Sales Report Filters"
      >
        {renderAdditionalFilters()}
      </ReportFilterModal>

      {/* Error Display */}
      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          closable
          showIcon
          onClose={() => setError(null)}
          style={{ marginBottom: '16px' }}
        />
      )}

      {/* Loading State */}
      {loading && (
        <Card size="small">
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <Spin size="large" />
            <div style={{ marginTop: '16px' }}>
              <Text>Loading sales data...</Text>
            </div>
          </div>
        </Card>
      )}

      {/* Summary Statistics */}
      {!loading && data && (
        <>
          <Row gutter={[8, 8]} style={{ marginBottom: '16px' }}>
            <Col xs={12} sm={12} md={6}>
              <Card size="small" bodyStyle={{ padding: isMobile ? '12px' : '20px' }}>
                <Statistic
                  title={<span style={{ fontSize: isMobile ? '12px' : '14px' }}>Total Revenue</span>}
                  value={data.summary?.total_revenue || 0}
                  precision={2}
                  prefix="₹"
                  valueStyle={{ color: '#3f8600', fontSize: isMobile ? '16px' : '20px' }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={12} md={6}>
              <Card size="small" bodyStyle={{ padding: isMobile ? '12px' : '20px' }}>
                <Statistic
                  title={<span style={{ fontSize: isMobile ? '12px' : '14px' }}>Items Sold</span>}
                  value={data.summary?.total_items_sold || 0}
                  precision={0}
                  prefix={<ShoppingCartOutlined />}
                  valueStyle={{ fontSize: isMobile ? '16px' : '20px' }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={12} md={6}>
              <Card size="small" bodyStyle={{ padding: isMobile ? '12px' : '20px' }}>
                <Statistic
                  title={<span style={{ fontSize: isMobile ? '12px' : '14px' }}>Total Invoices</span>}
                  value={data.summary?.total_invoices || 0}
                  prefix={<BarChartOutlined />}
                  valueStyle={{ fontSize: isMobile ? '16px' : '20px' }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={12} md={6}>
              <Card size="small" bodyStyle={{ padding: isMobile ? '12px' : '20px' }}>
                <Statistic
                  title={<span style={{ fontSize: isMobile ? '12px' : '14px' }}>Top Product</span>}
                  value={data.summary?.top_product || 'N/A'}
                  prefix={<TrophyOutlined />}
                  valueStyle={{ fontSize: isMobile ? '12px' : '16px' }}
                />
              </Card>
            </Col>
          </Row>

          {/* Tax Summary Row */}
          {(data.summary?.total_tax1 > 0 || data.summary?.total_tax2 > 0 || data.summary?.total_tax3 > 0) && (
            <Row gutter={[8, 8]} style={{ marginBottom: '16px' }}>
              <Col xs={12} sm={12} md={6}>
                <Card size="small" style={{ borderLeft: '3px solid #1890ff' }} bodyStyle={{ padding: isMobile ? '12px' : '20px' }}>
                  <Statistic
                    title={<span style={{ fontSize: isMobile ? '11px' : '14px' }}>{data.summary?.tax_rate1_name || 'Tax 1'}</span>}
                    value={data.summary?.total_tax1 || 0}
                    precision={2}
                    prefix="₹"
                    valueStyle={{ color: '#1890ff', fontSize: isMobile ? '14px' : '18px' }}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={12} md={6}>
                <Card size="small" style={{ borderLeft: '3px solid #722ed1' }} bodyStyle={{ padding: isMobile ? '12px' : '20px' }}>
                  <Statistic
                    title={<span style={{ fontSize: isMobile ? '11px' : '14px' }}>{data.summary?.tax_rate2_name || 'Tax 2'}</span>}
                    value={data.summary?.total_tax2 || 0}
                    precision={2}
                    prefix="₹"
                    valueStyle={{ color: '#722ed1', fontSize: isMobile ? '14px' : '18px' }}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={12} md={6}>
                <Card size="small" style={{ borderLeft: '3px solid #eb2f96' }} bodyStyle={{ padding: isMobile ? '12px' : '20px' }}>
                  <Statistic
                    title={<span style={{ fontSize: isMobile ? '11px' : '14px' }}>{data.summary?.tax_rate3_name || 'Tax 3'}</span>}
                    value={data.summary?.total_tax3 || 0}
                    precision={2}
                    prefix="₹"
                    valueStyle={{ color: '#eb2f96', fontSize: isMobile ? '14px' : '18px' }}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={12} md={6}>
                <Card size="small" style={{ borderLeft: '3px solid #fa541c' }} bodyStyle={{ padding: isMobile ? '12px' : '20px' }}>
                  <Statistic
                    title={<span style={{ fontSize: isMobile ? '11px' : '14px' }}>Total Tax</span>}
                    value={data.summary?.total_tax || 0}
                    precision={2}
                    prefix="₹"
                    valueStyle={{ color: '#fa541c', fontSize: isMobile ? '14px' : '18px' }}
                  />
                </Card>
              </Col>
            </Row>
          )}

          {/* Grand Total with Tax */}
          {data.summary?.total_with_tax > 0 && (
            <Row gutter={[8, 8]} style={{ marginBottom: '16px' }}>
              <Col xs={24}>
                <Card size="small" style={{ background: '#f6ffed', borderColor: '#b7eb8f' }}>
                  <Row justify="space-between" align="middle">
                    <Col>
                      <Text strong style={{ fontSize: isMobile ? '14px' : '16px' }}>Grand Total (Revenue + Tax)</Text>
                    </Col>
                    <Col>
                      <Text strong style={{ fontSize: isMobile ? '18px' : '24px', color: '#52c41a' }}>
                        ₹{data.summary?.total_with_tax?.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </Text>
                    </Col>
                  </Row>
                </Card>
              </Col>
            </Row>
          )}

          {/* Monthly Sales Chart */}
          {data.monthly_revenue && data.monthly_revenue.length > 0 && (
            <Card
              size="small"
              style={{ marginBottom: '16px' }}
              title={<Text strong>Monthly Sales Revenue</Text>}
            >
              <ResponsiveContainer width="100%" height={isMobile ? 200 : 300}>
                <LineChart
                  data={data.monthly_revenue}
                  margin={{ top: 20, right: isMobile ? 10 : 30, left: isMobile ? 0 : 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="month"
                    tick={{ fontSize: isMobile ? 10 : 12 }}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis
                    tickFormatter={(value) => `₹${(value / 1000).toFixed(0)}K`}
                    tick={{ fontSize: isMobile ? 10 : 12 }}
                    width={isMobile ? 50 : 60}
                  />
                  <Tooltip
                    formatter={(value) => [`₹${value?.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, 'Revenue']}
                    labelFormatter={(label) => `Month: ${label}`}
                  />
                  <Line
                    type="monotone"
                    dataKey="revenue"
                    stroke="#52c41a"
                    strokeWidth={2}
                    dot={{ fill: '#52c41a', strokeWidth: 2, r: isMobile ? 3 : 4 }}
                    activeDot={{ r: isMobile ? 5 : 6, fill: '#52c41a' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Card>
          )}

          {/* Invoice Details Table */}
          <Card
            size="small"
            style={{ marginBottom: '16px' }}
            title={
              <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
                <div>
                  <Text strong>Invoice Details</Text>
                  {!isMobile && (
                    <Text type="secondary" style={{ fontSize: '13px', marginLeft: '12px' }}>
                      {data.summary?.date_range || 'N/A'}
                    </Text>
                  )}
                </div>
                <Button
                  type="primary"
                  size="small"
                  icon={<DownloadOutlined />}
                  onClick={handleExportModalOpen}
                >
                  Export
                </Button>
              </div>
            }
          >
            <Table
              columns={columns}
              dataSource={data.invoices || []}
              rowKey={(record) => record.invoice_id}
              size="small"
              pagination={{
                defaultPageSize: isMobile ? 5 : 10,
                pageSizeOptions: ['5', '10', '20', '50', '100'],
                showSizeChanger: !isMobile,
                hideOnSinglePage: false,
                showTotal: isMobile ? undefined : (total, range) => `${range[0]}-${range[1]} of ${total}`,
                position: ['bottomRight'],
                responsive: true,
                simple: isMobile,
                style: { marginTop: '16px', marginBottom: '8px' }
              }}
              scroll={{ x: 'max-content' }}
            />
          </Card>
        </>
      )}

      {/* Export Modal */}
      <Modal
        title="Export Sales Report"
        open={exportModalVisible}
        onCancel={() => !exporting && setExportModalVisible(false)}
        footer={null}
        width={isMobile ? '100%' : 500}
        closable={!exporting}
        maskClosable={!exporting}
        style={isMobile ? { top: 20, margin: '0 12px' } : undefined}
      >
        <div style={{ padding: '20px 0' }}>
          {exportStatus && (exportStatus.status === 'PENDING' || exportStatus.status === 'PROCESSING') && (
            <div style={{ marginBottom: '24px', padding: '16px', background: '#f5f5f5', borderRadius: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <Text strong>Export in Progress</Text>
                <Button type="primary" danger size="small" icon={<StopOutlined />} onClick={handleAbortExport}>
                  Abort
                </Button>
              </div>
              <Progress percent={exportStatus.progress || 0} status="active" style={{ marginTop: '8px' }} />
              <Text type="secondary" style={{ fontSize: '12px' }}>
                Processing {exportStatus.processed_invoices || 0} of {exportStatus.total_invoices} invoices...
              </Text>
            </div>
          )}

          {exportStatus && exportStatus.status === 'COMPLETED' && (
            <div style={{ marginBottom: '24px', padding: '16px', background: '#f6ffed', borderRadius: '8px', border: '1px solid #b7eb8f' }}>
              <Space>
                <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '20px' }} />
                <div>
                  <Text strong style={{ color: '#52c41a' }}>Previous Export Completed</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    {exportStatus.total_invoices} invoice(s) exported on {dayjs(exportStatus.completed_at).format('MMM D, YYYY h:mm A')}
                  </Text>
                </div>
              </Space>
              <div style={{ marginTop: '12px' }}>
                <Button type="primary" size="small" icon={<DownloadOutlined />} onClick={handleDownloadExport}>
                  Download Again
                </Button>
              </div>
            </div>
          )}

          {exportStatus && exportStatus.status === 'FAILED' && (
            <div style={{ marginBottom: '24px', padding: '16px', background: '#fff2f0', borderRadius: '8px', border: '1px solid #ffccc7' }}>
              <Space>
                <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: '20px' }} />
                <div>
                  <Text strong style={{ color: '#ff4d4f' }}>Previous Export Failed</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    {exportStatus.error_message || 'An error occurred during export'}
                  </Text>
                </div>
              </Space>
            </div>
          )}

          {!exporting && (
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <div>
                <Button
                  icon={<FileExcelOutlined />}
                  onClick={() => handleExport('excel')}
                  type="primary"
                  size="large"
                  block
                  style={{ background: '#52c41a', borderColor: '#52c41a' }}
                >
                  Download as Excel
                </Button>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  Export sales data as spreadsheet
                </Text>
              </div>
              <div>
                <Button
                  icon={<FileZipOutlined />}
                  onClick={() => handleExport('zip')}
                  size="large"
                  block
                  disabled={!data?.invoices?.length || (exportStatus?.status === 'PENDING' || exportStatus?.status === 'PROCESSING')}
                >
                  Export Invoice PDFs (ZIP)
                </Button>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  {data?.invoices?.length
                    ? `Export ${[...new Set(data.invoices.map(inv => inv.invoice_id))].length} invoice PDF(s) as ZIP (async)`
                    : 'No invoices available to export'}
                </Text>
              </div>
            </Space>
          )}

          {exporting && (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <Spin size="large" />
              <div style={{ marginTop: '16px' }}>
                <Text>Starting export... Please wait.</Text>
              </div>
              <div style={{ marginTop: '16px' }}>
                <Button type="primary" danger icon={<StopOutlined />} onClick={handleAbortExport}>
                  Abort Export
                </Button>
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* No Data State */}
      {!loading && !data && !error && (
        <Card size="small">
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <Text type="secondary">No data available. Please select filters above.</Text>
          </div>
        </Card>
      )}

      {/* Empty Data State */}
      {!loading && data && data.invoices?.length === 0 && (
        <Card size="small">
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <Text type="secondary">No sales data found for the selected period.</Text>
          </div>
        </Card>
      )}
    </div>
  );
};

export default SalesReport;
